import { useState, useMemo } from 'react'

import {
	EuiButton,
	EuiModalBody,
	EuiModalFooter,
} from '@equipmentshare/ds2'

import AddList from '../../../../components/AddList'
import { useAllPermissionsQuery, Role, PermissionMeta, Permission } from '../../../../../../../graphql'
import useAssignPermissionsHandler from '../../../effects/assign-permissions-to-role'

import styles from './addPermissions.module.css'
import { heapEvent } from '@equipmentshare/heaper'

export type AddPermissionsModalProps = {
	onCancel: () => void,
	onSuccess: () => void,
	role: Role,
}

export default function AddPermissionsModal( { onCancel, onSuccess, role }: AddPermissionsModalProps ) {
	const [ isSaving, setIsSaving ] = useState( false )
	const [ selectedPermissions, setSelectedPermissions ] = useState<PermissionMeta[]>( [] )

	const { data: { allPermissions } = {}, loading } = useAllPermissionsQuery()
	const assignPermissions = useAssignPermissionsHandler()

	const filteredPermissions = useMemo<PermissionMeta[]>( () => {
		if ( allPermissions?.length ) {
			return allPermissions.filter( ( permission ) => {
				const existsOnRole = role.permissions.some( ( p ) => p?.id === permission.id )
				const alreadySelected = selectedPermissions.some( ( p ) => p.id === permission.id )

				return !existsOnRole && !alreadySelected
			} ) as PermissionMeta[]
		}

		return []
	}, [ allPermissions, role ] )

	const handleSubmit = async() => {
		heapEvent( 'Inventory - Add Permissions to Role Modal - Click - Submit', {} )
		setIsSaving( true )
		const permissions = ( selectedPermissions.filter( ( pm ) => !!pm.name ).map( ( pm ) => pm.name ) ?? [] ) as Permission[]
		await assignPermissions( role, permissions )
		setIsSaving( false )
		onSuccess()
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Add Permissions to Role Modal - Click - Cancel', {} )
		onCancel()
	}

	return (
		<>
			<EuiModalBody>
				<AddList
					itemList={filteredPermissions}
					itemRender={( p ) => (
						<div className={styles.item}>
							<div>{p.friendlyName}</div>
							<div className={styles.rightAlign}>{p.name}</div>
						</div>
					)}
					getName={( p ) => p.friendlyName}
					searchPlaceholder="Search for a permission..."
					valueKey="id"
					isLoading={loading}
					selectedItems={selectedPermissions}
					setSelectedItems={setSelectedPermissions}
				/>
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButton
					id="cancelBtn"
					color="text"
					onClick={handleCancel}
				>
					Cancel
				</EuiButton>
				<EuiButton
					id="submitBtn"
					fill
					onClick={handleSubmit}
					isLoading={isSaving}
				>
					Submit
				</EuiButton>
			</EuiModalFooter>
		</>
	)
}
